import React, { useState, useContext, useRef, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import Button from "../../components/button/Button";
import UseAlert from "../../components/alert/UseAlert";
import { AppContext } from "../../context/AppContext";
import { signInverifyMFATokenAPI } from "../../utils/api";
import AuthLayout from "./AuthLayout";
import "./Authentication.css";

const MFASignIn = (props) => {
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [MFAToken, setMFAToken] = useState("");
  const [alert, alert_notify] = UseAlert();
  const context = useContext(AppContext);
  const setIsLoginPage = context.setIsLoginPage;
  const inputsRef = useRef([]);

  // Redirect if session_key or email missing
  useEffect(() => {
    const session_key = sessionStorage.getItem("session_key");
    const email = localStorage.getItem("email");
    if (!session_key || !email) {
      alert_notify("error", "Session expired. Please sign in again.");
      props.history.push("/login");
    }
  }, [alert_notify, props.history]);

  const verifyMFAToken = async (e) => {
    try {
      e.preventDefault();
      setLoadingStatus(true);

      if (MFAToken === "") {
        throw new Error("Please enter the MFA code.");
      }

      if (MFAToken.length !== 6) {
        throw new Error("MFA code must be 6 digits.");
      }

      const session_key = sessionStorage.getItem("session_key");
      const email = localStorage.getItem("email");

      if (!session_key || !email) {
        throw new Error("Session expired. Please sign in again.");
      }

      const body = {
        session_key,
        mfa_token: MFAToken,
        email,
      };

      const data = await signInverifyMFATokenAPI(body);

      if (data.status) {
        localStorage.setItem("access_token", data.data.access_token);
        localStorage.setItem("refresh_token", data.data.refresh_token);
        setIsLoginPage(false);
        props.history.push("/transaction-list");
      } else {
        if (data.response_code === 401) {
          throw new Error("Invalid MFA code. Please try again.");
        } else {
          throw new Error(data.data || "Verification failed.");
        }
      }
    } catch (e) {
      alert_notify("error", e?.message || "Something went wrong.");
    } finally {
      setLoadingStatus(false);
    }
  };

  return (
    <AuthLayout alert={alert} className="mfa-container forgot-password-form-body with-login-text">
      <div className="login-messages">
        <span className="login-welcome">Multi-factor Authentication</span>
        <div className="login-text-v2 mobile-only">Enter an MFA code to complete sign-in</div>
      </div>
      <div className="form-container">
        <form id="login_form" onSubmit={verifyMFAToken}>
          <Row bsPrefix="col">
            <Col className="login-col mfa-login-input">
              <label className="login-label-v2 web-only">Enter an MFA code to complete sign-in</label>
              <div className="mfa-code-boxes">
                {[...Array(6)].map((_, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="mfa-box"
                    value={MFAToken[i] || ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      if (val) {
                        const newToken = MFAToken.split("");
                        newToken[i] = val[0];
                        const updated = newToken.join("").slice(0, 6);
                        setMFAToken(updated);
                        if (i < 5) inputsRef.current[i + 1]?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace") {
                        e.preventDefault();
                        const newToken = MFAToken.split("");
                        if (MFAToken[i]) {
                          newToken[i] = "";
                          setMFAToken(newToken.join(""));
                        } else if (i > 0) {
                          inputsRef.current[i - 1]?.focus();
                          const backToken = MFAToken.split("");
                          backToken[i - 1] = "";
                          setMFAToken(backToken.join(""));
                        }
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                      if (paste.length === 0) return;
                      setMFAToken(paste);
                      paste.split("").forEach((char, idx) => {
                        if (inputsRef.current[idx]) {
                          inputsRef.current[idx].value = char;
                        }
                      });
                      inputsRef.current[Math.min(paste.length, 5)]?.focus();
                    }}
                  />
                ))}
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex">
              <Button className="primary btn_login" type="submit">
                {!loadingStatus ? "Verify" : <Spinner animation="border" role="status" className="spinner" />}
              </Button>
            </Col>
          </Row>
        </form>
      </div>
    </AuthLayout>
  );
};

export default MFASignIn;
